/**
 * Per-tenant version-pin control (OCP-485, Phase E item 19).
 *
 * Self-contained: fetches its own state from `GET /v1/admin/tenants/:id/
 * pinning` and writes via `PATCH .../pinning`, so it drops onto the tenant
 * detail page as a single `<PinningPanel tenantId=... />` without depending on
 * the parent's tenant payload. Kept deliberately decoupled from the
 * version-display / quota / manual-update work on the same page (OCP-484) so
 * the two land without stepping on each other.
 *
 * A tenant is either on `latest` (rides the fleet rolling tag; picked up by
 * every maintenance window) or `pinned` to an explicit image tag (skipped by
 * the window fan-out with a `skipped_pinned` event). A pin must be a clean
 * X.Y.Z registry tag >= the min-web floor - the API enforces that and returns
 * `invalid_pinning` on a bad tag, surfaced inline here.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api, ApiError } from '../lib/api';

interface PinningState {
  tenant_id: string;
  update_channel: 'latest' | 'pinned';
  image_tag: string | null;
  resolved_image_tag: string;
  rolling_image_tag: string;
  min_web_image_tag: string;
}

export function PinningPanel({ tenantId }: { tenantId: string }) {
  const [state, setState] = useState<PinningState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Draft form state.
  const [channel, setChannel] = useState<'latest' | 'pinned'>('latest');
  const [tag, setTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api<PinningState>(`/v1/admin/tenants/${tenantId}/pinning`);
      setState(r);
      setChannel(r.update_channel);
      setTag(r.image_tag ?? '');
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? `Could not load pin state (HTTP ${err.status}).` : 'Could not load pin state.',
      );
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty =
    state !== null &&
    (channel !== state.update_channel ||
      (channel === 'pinned' && tag.trim() !== (state.image_tag ?? '')));

  const submit = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const r = await api<PinningState>(`/v1/admin/tenants/${tenantId}/pinning`, {
        method: 'PATCH',
        body: {
          update_channel: channel,
          image_tag: channel === 'pinned' ? tag.trim() : null,
        },
      });
      setState(r);
      setChannel(r.update_channel);
      setTag(r.image_tag ?? '');
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; reason?: string } | null;
        if (err.status === 400 && body?.error === 'invalid_pinning') {
          setSaveError(body.reason ?? 'Invalid image tag.');
        } else {
          setSaveError(`Could not save pin (HTTP ${err.status}).`);
        }
      } else {
        setSaveError('Could not save pin.');
      }
    } finally {
      setSaving(false);
    }
  }, [tenantId, channel, tag]);

  return (
    <section className="mt-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Version pinning</h2>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Controls which image tag this tenant runs.{' '}
        <span className="font-medium">Latest</span> rides the fleet rolling tag and is rolled by
        every maintenance window; <span className="font-medium">pinned</span> holds an explicit
        version and is skipped by the window fan-out.
      </p>

      {loadError && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      {state && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StateField label="Channel">
              <StatusPill channel={state.update_channel} />
            </StateField>
            <StateField label="Running version">
              <span className="font-mono text-sm">{state.resolved_image_tag}</span>
            </StateField>
            <StateField label="Fleet rolling tag">
              <span className="font-mono text-sm text-gray-500">{state.rolling_image_tag}</span>
            </StateField>
          </div>

          <div className="mt-5 space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="pin-channel"
                  value="latest"
                  checked={channel === 'latest'}
                  onChange={() => setChannel('latest')}
                />
                <span>Latest (auto-update)</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="pin-channel"
                  value="pinned"
                  checked={channel === 'pinned'}
                  onChange={() => setChannel('pinned')}
                />
                <span>Pinned</span>
              </label>
            </div>

            {channel === 'pinned' && (
              <label className="block max-w-xs text-sm">
                <span className="text-gray-700 dark:text-gray-300">Image tag</span>
                <input
                  type="text"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder={state.rolling_image_tag}
                  aria-label="Pinned image tag"
                  autoComplete="off"
                  className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 font-mono text-sm focus:border-brand-500 focus:outline-none"
                />
                <span className="mt-1 block text-xs text-gray-500">
                  Plain X.Y.Z, no v-prefix, &gt;= {state.min_web_image_tag}.
                </span>
              </label>
            )}

            {saveError && (
              <div
                role="alert"
                className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              >
                {saveError}
              </div>
            )}
            {saved && !dirty && (
              <div className="text-sm font-medium text-green-700 dark:text-green-400">Saved.</div>
            )}

            <button
              type="button"
              onClick={() => void submit()}
              disabled={saving || !dirty || (channel === 'pinned' && tag.trim() === '')}
              className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save pin'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function StateField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StatusPill({ channel }: { channel: 'latest' | 'pinned' }) {
  return channel === 'pinned' ? (
    <span className="inline-flex items-center rounded bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 ring-1 ring-inset ring-amber-200 dark:ring-amber-800">
      pinned
    </span>
  ) : (
    <span className="inline-flex items-center rounded bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-200 dark:ring-green-800">
      latest
    </span>
  );
}
